import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FiAlertTriangle } from 'react-icons/fi';
import { useAuth } from '../../../context/AuthContext';
import { formatDate } from '../../../utils/dateUtils';
import CategoryBalancesTab from './CategoryBalancesTab';
import LedgerBalancesTab from './LedgerBalancesTab';
import OfferingBalancesTab from './OfferingBalancesTab';
import OpeningBalanceHeader from './OpeningBalanceHeader';
import OpeningBalanceReport from './OpeningBalanceReport';
import OpeningBalanceStatusModal from './OpeningBalanceStatusModal';
import styles from './OpeningBalance.module.css';
import { toPreviousDate } from './openingBalanceUtils';
import useOpeningBalanceData from './useOpeningBalanceData';

const tabLabels = {
  report: 'Report',
  assets: 'Ledger Balances',
  categories: 'Category Fund Balances',
  offerings: 'Offering Balances',
};

export default function OpeningBalancePage() {
  const { yearId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { activeDerasarId } = useAuth();
  const [activeTab, setActiveTab] = useState('report');
  const [statusModal, setStatusModal] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);

  const queryMode = new URLSearchParams(location.search).get('mode');
  const reportOnly = queryMode !== 'setup';

  const {
    fy,
    accounts,
    categories,
    sortedCategories,
    personUsers,
    loading,
    saving,
    canEdit,
    inputsEditable,
    assetLines,
    categoryLines,
    offeringRows,
    structureTotals,
    reportData,
    isLockedOpeningLedgerAccount,
    setAssetField,
    setCategoryField,
    addOfferingRowWithData,
    addOfferingRowsWithData,
    updateOfferingRow,
    removeOfferingRow,
    saveAssets,
    saveCategories,
    saveOfferings,
    handleLock,
    handleUnlock,
  } = useOpeningBalanceData({ activeDerasarId, yearId, reportOnly });

  const setupMode = queryMode === 'setup';
  // readOnlyReport = render as a report (carry-forward view), not the OB form.
  // True when not in setup mode, or this isn't the OB year.
  const readOnlyReport = !setupMode || !canEdit;
  // inputs accept edits only when OB state is 'draft' AND FY is unlocked.
  // When locked, inputs render disabled but the Unlock button is still available.
  const inputsReadOnly = readOnlyReport || !inputsEditable;
  const frozen = fy?.obStatus === 'frozen';
  const locked = fy?.obStatus === 'locked';
  const openingOfferDateMax = toPreviousDate(fy?.startDate);
  const offeringTabVisible = !readOnlyReport;

  const openLockModal = () => setStatusModal('lock');
  const openUnlockModal = () => setStatusModal('unlock');
  const closeStatusModal = () => setStatusModal(null);

  const handleStatusConfirm = async () => {
    setStatusBusy(true);
    try {
      if (statusModal === 'lock') {
        await handleLock();
      }
      if (statusModal === 'unlock') {
        await handleUnlock();
      }
      closeStatusModal();
    } finally {
      setStatusBusy(false);
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!fy) return <div className="empty-state">Financial year not found.</div>;

  const tabs = [
    ['report', tabLabels.report],
    ['assets', tabLabels.assets],
    ['categories', tabLabels.categories],
    ...(offeringTabVisible ? [['offerings', tabLabels.offerings]] : []),
  ];

  return (
    <div className={styles.page}>
      <OpeningBalanceHeader
        navigate={navigate}
        title={readOnlyReport ? 'Opening Balance Report' : 'Opening Balance Setup'}
        yearLabel={fy.yearLabel}
        status={fy.obStatus}
        frozen={frozen}
        locked={locked}
        balanceDifference={fy.balanceDifference}
        readOnlyReport={readOnlyReport}
        onLock={openLockModal}
        onUnlock={openUnlockModal}
      />

      {frozen && (
        <div className={`${styles.infoBox} ${styles.infoBoxWarn}`} style={{ marginBottom: 16 }}>
          <FiAlertTriangle />
          <span>This opening balance is frozen because the financial year is locked. No further edits are allowed.</span>
        </div>
      )}

      {!readOnlyReport && activeTab !== 'report' && openingOfferDateMax && (
        <div className={`${styles.infoBox} ${styles.infoBoxWarn}`} style={{ marginBottom: 16 }}>
          <FiAlertTriangle />
          <span>
            Opening offering dates must be before {formatDate(fy.startDate)}. Current-year and future dates are not allowed here.
          </span>
        </div>
      )}

      <div className={styles.tabBar}>
        {tabs.map(([tabId, label]) => (
          <button
            key={tabId}
            className={`${styles.tab} ${activeTab === tabId ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(tabId)}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'report' && <OpeningBalanceReport data={reportData} />}

      {activeTab === 'assets' && (
        <LedgerBalancesTab
          accounts={accounts}
          assetLines={assetLines}
          readOnly={inputsReadOnly}
          frozen={frozen}
          saving={saving}
          onSave={saveAssets}
          onFieldChange={setAssetField}
          isLockedAccount={isLockedOpeningLedgerAccount}
        />
      )}

      {activeTab === 'categories' && (
        <CategoryBalancesTab
          categories={categories}
          categoryLines={categoryLines}
          total={structureTotals.categoryFundTotal}
          readOnly={true}
          frozen={frozen}
          saving={saving}
          onSave={saveCategories}
          onFieldChange={setCategoryField}
          derivedHint="Category Fund Balance is auto-derived from the Donation Income ledger opening balance. Edit ledger balances above to change."
        />
      )}

      {activeTab === 'offerings' && (
        <OfferingBalancesTab
          fy={fy}
          categories={categories}
          sortedCategories={sortedCategories}
          personUsers={personUsers}
          offeringRows={offeringRows}
          readOnly={inputsReadOnly}
          frozen={frozen}
          saving={saving}
          openingOfferDateMax={openingOfferDateMax}
          total={structureTotals.offeringBalanceTotal}
          onAddRowWithData={addOfferingRowWithData}
          onAddRowsWithData={addOfferingRowsWithData}
          onUpdateRow={updateOfferingRow}
          onRemoveRow={removeOfferingRow}
          onSave={saveOfferings}
        />
      )}

      <OpeningBalanceStatusModal
        mode={statusModal}
        open={!!statusModal}
        busy={statusBusy}
        onClose={closeStatusModal}
        onConfirm={handleStatusConfirm}
      />
    </div>
  );
}
